import { createApiClient } from '@numistic/api-client';
import { ORPCError } from '@orpc/client';
import { createAuthClient } from 'better-auth/client';
import { DataError } from './data-client.js';
export function createHttpDataClient(options) {
    const api = createApiClient(options);
    const auth = createAuthClient({
        baseURL: options.origin,
        basePath: '/api/auth',
        fetchOptions: { credentials: 'include' },
    });
    /** Вызов oRPC с приведением ошибок к DataError. */
    async function call(fn) {
        try {
            return await fn();
        }
        catch (error) {
            throw toDataError(error);
        }
    }
    /** Вызов Better Auth ({ data, error }) с приведением ошибок к DataError. */
    async function authCall(fn) {
        let result;
        try {
            result = await fn();
        }
        catch (error) {
            throw new DataError('NETWORK', 0, String(error));
        }
        if (result.error) {
            const { status, code, message } = result.error;
            throw new DataError(status === 429 ? 'RATE_LIMITED' : (code ?? httpCode(status)), status, message);
        }
        return result.data;
    }
    const refApi = (kind) => api.ref[kind];
    return {
        system: {
            async readiness() {
                try {
                    return await api.health.ready();
                }
                catch (error) {
                    // 503 от API несёт полный отчёт о проверках — отдаём его UI как данные.
                    if (error instanceof ORPCError && error.status === 503 && error.data) {
                        return error.data;
                    }
                    throw toDataError(error);
                }
            },
        },
        auth: {
            async getSession() {
                const data = await authCall(() => auth.getSession());
                if (!data)
                    return null;
                const user = data.user;
                return {
                    user: { id: user.id, name: user.name, email: user.email, role: user.role },
                    session: { id: data.session.id, expiresAt: new Date(data.session.expiresAt) },
                };
            },
            async signIn(input) {
                await authCall(() => auth.signIn.email({ email: input.email, password: input.password }));
            },
            async signOut() {
                await authCall(() => auth.signOut());
            },
            async requestPasswordReset(input) {
                await authCall(() => auth.requestPasswordReset(input));
            },
            async resetPassword(input) {
                await authCall(() => auth.resetPassword(input));
            },
            async listSessions() {
                const [sessions, current] = await Promise.all([
                    authCall(() => auth.listSessions()),
                    authCall(() => auth.getSession()),
                ]);
                return (sessions ?? []).map((s) => ({
                    id: s.id,
                    token: s.token,
                    userAgent: s.userAgent ?? null,
                    ipAddress: s.ipAddress ?? null,
                    createdAt: new Date(s.createdAt),
                    expiresAt: new Date(s.expiresAt),
                    current: s.id === current?.session.id,
                }));
            },
            async revokeSession(token) {
                await authCall(() => auth.revokeSession({ token }));
            },
        },
        invites: {
            inspect: (token) => call(() => api.invites.inspect({ token })),
            async accept(input) {
                await call(() => api.invites.accept(input));
            },
        },
        meta: {
            get: () => call(() => api.meta.get()),
        },
        refs: {
            // Процедуры справочников однотипны; вид справочника выбирается по ключу контракта.
            list: (kind, options) => call(() => refApi(kind).list(options?.includeInactive ? { includeInactive: 'true' } : {})),
            create: (kind, input) => call(() => refApi(kind).create(input)),
            update: (kind, id, patch) => call(() => refApi(kind).update({ id, ...patch })),
        },
        catalog: {
            list: (input) => call(() => api.catalog.list(input)),
            get: (id) => call(() => api.catalog.get({ id })),
            create: (input) => call(() => api.catalog.create(input)),
            update: (input) => call(() => api.catalog.update(input)),
            setVisibility: (id, visibility) => call(() => api.catalog.setVisibility({ id, visibility })),
            bulk: (input) => call(() => api.catalog.bulk(input)),
            history: (id) => call(() => api.catalog.history({ id })),
            changes: (input) => call(() => api.catalog.changes(input)),
            async remove(id) {
                await call(() => api.catalog.remove({ id }));
            },
            createVariant: (itemId, input) => call(() => api.catalog.variants.create({ itemId, ...input })),
            updateVariant: (id, input) => call(() => api.catalog.variants.update({ id, ...input })),
            async removeVariant(id) {
                await call(() => api.catalog.variants.remove({ id }));
            },
            attachImage: (itemId, input) => call(() => api.catalog.images.attach({ itemId, ...input })),
            updateImage: (id, input) => call(() => api.catalog.images.update({ id, ...input })),
            async removeImage(id) {
                await call(() => api.catalog.images.remove({ id }));
            },
        },
        media: {
            upload: (file) => call(() => api.media.upload({ file })),
        },
        collections: {
            tree: () => call(() => api.collections.tree()),
            create: (input) => call(() => api.collections.create({ description: null, ...input })),
            update: (input) => call(() => api.collections.update(input)),
            async remove(id) {
                await call(() => api.collections.remove({ id }));
            },
        },
        locations: {
            tree: () => call(() => api.locations.tree()),
            create: (input) => call(() => api.locations.create({ kind: 'other', ...input })),
            update: (input) => call(() => api.locations.update(input)),
            async remove(id) {
                await call(() => api.locations.remove({ id }));
            },
        },
        specimens: {
            list: (input) => call(() => api.specimens.list(input)),
            get: (id) => call(() => api.specimens.get({ id })),
            create: (input) => call(() => api.specimens.create(input)),
            update: (input) => call(() => api.specimens.update(input)),
            async remove(id) {
                await call(() => api.specimens.remove({ id }));
            },
            duplicate: (id) => call(() => api.specimens.duplicate({ id })),
            bulk: (input) => call(() => api.specimens.bulk(input)),
            createEvent: (specimenId, input) => call(() => api.specimens.events.create({ specimenId, ...input })),
            async removeEvent(id) {
                await call(() => api.specimens.events.remove({ id }));
            },
            createValuation: (specimenId, input) => call(() => api.specimens.valuations.create({ specimenId, ...input })),
            async removeValuation(id) {
                await call(() => api.specimens.valuations.remove({ id }));
            },
            createNote: (specimenId, bodyMd) => call(() => api.specimens.notes.create({ specimenId, bodyMd })),
            updateNote: (id, bodyMd) => call(() => api.specimens.notes.update({ id, bodyMd })),
            async removeNote(id) {
                await call(() => api.specimens.notes.remove({ id }));
            },
            attachImage: (specimenId, input) => call(() => api.specimens.images.attach({ specimenId, ...input })),
            copyCatalogImages: (specimenId) => call(() => api.specimens.images.copyFromCatalog({ specimenId })),
        },
        tags: {
            list: () => call(() => api.tags.list()),
            create: (input) => call(() => api.tags.create(input)),
            update: (input) => call(() => api.tags.update(input)),
            async remove(id) {
                await call(() => api.tags.remove({ id }));
            },
        },
        counterparties: {
            list: () => call(() => api.counterparties.list()),
            create: (input) => call(() => api.counterparties.create(input)),
            update: (input) => call(() => api.counterparties.update(input)),
            async remove(id) {
                await call(() => api.counterparties.remove({ id }));
            },
        },
        savedFilters: {
            list: () => call(() => api.savedFilters.list()),
            create: (input) => call(() => api.savedFilters.create(input)),
            async remove(id) {
                await call(() => api.savedFilters.remove({ id }));
            },
        },
        stats: {
            summary: (scope) => call(() => api.stats.summary(scope)),
            breakdown: (input) => call(() => api.stats.breakdown(input)),
            counts: (groupBy) => call(() => api.stats.counts({ groupBy })),
        },
        shares: {
            list: (collectionId) => call(() => api.shares.list({ collectionId })),
            create: (input) => call(() => api.shares.create(input)),
            async revoke(id) {
                await call(() => api.shares.revoke({ id }));
            },
        },
        publicShare: {
            get: (token) => call(() => api.publicShare.get({ token })),
            specimens: (input) => call(() => api.publicShare.specimens(input)),
            specimen: (token, id) => call(() => api.publicShare.specimen({ token, id })),
        },
        io: {
            exportUrl(format, scope = {}) {
                const query = new URLSearchParams({ format });
                for (const [k, v] of Object.entries(scope))
                    if (v !== undefined)
                        query.set(k, String(v));
                return `${options.origin}/api/export?${query}`;
            },
            csvColumns: (file) => call(() => api.io.csvColumns({ file })),
            importCsv: (file, mapping, dryRun) => call(() => api.io.importCsv({
                file,
                mapping: JSON.stringify(mapping),
                dryRun: String(dryRun),
            })),
            importJson: (file, dryRun) => call(() => api.io.importJson({ file, dryRun: String(dryRun) })),
        },
        profile: {
            get: () => call(() => api.profile.get()),
            update: (input) => call(() => api.profile.update(input)),
        },
        numista: {
            status: () => call(() => api.numista.status()),
            search: (input) => call(() => api.numista.search(input)),
            issuers: () => call(() => api.numista.issuers()),
            catalogues: () => call(() => api.numista.catalogues()),
            preview: (numistaId) => call(() => api.numista.preview({ numistaId })),
            import: (input) => call(() => api.numista.import(input)),
        },
        adminMeta: {
            get: () => call(() => api.adminMeta.get()),
            async mutate(group, op, input) {
                const procedures = api.adminMeta[group];
                await call(() => procedures[op](input));
            },
        },
        admin: {
            listUsers: () => call(() => api.admin.users.list()),
            listInvites: () => call(() => api.admin.invites.list()),
            createInvite: (input) => call(() => api.admin.invites.create(input)),
            async revokeInvite(id) {
                await call(() => api.admin.invites.revoke({ id }));
            },
            getSettings: () => call(() => api.admin.settings.get()),
            async updateSettings(input) {
                await call(() => api.admin.settings.update(input));
            },
        },
    };
}
function httpCode(status) {
    if (status === 401)
        return 'UNAUTHORIZED';
    if (status === 403)
        return 'FORBIDDEN';
    return 'UNKNOWN';
}
function toDataError(error) {
    if (error instanceof DataError)
        return error;
    if (error instanceof ORPCError) {
        const issues = error.data?.issues ?? [];
        return new DataError(error.code, error.status, error.message, issues);
    }
    if (error instanceof TypeError)
        return new DataError('NETWORK', 0, error.message);
    return new DataError('UNKNOWN', 0, String(error));
}
//# sourceMappingURL=http-data-client.js.map